import type { Stop } from './api';

/**
 * Customer-list rules shared by the Customers tab and the route page:
 * search matching, column sorting, address text and the individual map
 * link. Everything here is plain data logic with no DOM access, so the
 * client test suite covers it directly.
 */

/** One customer row together with the route and distributor it belongs to. */
export interface CustomerRow {
  distributorId: number;
  distributorName: string;
  routeNumber: string;
  stop: Stop;
}

export type SortKey = 'name' | 'street' | 'place' | 'contact' | 'phone';
export type SortDirection = 'ascending' | 'descending';

export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

export const SORT_LABELS: Record<SortKey, string> = {
  name: 'Customer',
  street: 'Street address',
  place: 'City, state, ZIP',
  contact: 'Contact',
  phone: 'Phone',
};

export const SORT_KEYS = Object.keys(SORT_LABELS) as SortKey[];

// Natural order: "9 Main St" sorts before "10 Main St"; case and accents are ignored.
const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

const text = (value: string | null | undefined): string => value?.trim() ?? '';
const lower = (value: string | null | undefined): string => text(value).toLocaleLowerCase();
const digitsOf = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '');

/** The comparison fields for one column; the first decides whether the value is blank. */
function sortParts(stop: Stop, key: SortKey): string[] {
  switch (key) {
    case 'name':
      return [text(stop.name)];
    case 'street':
      return [text(stop.streetAddress), text(stop.city)];
    case 'place':
      return [text(stop.city), text(stop.state), text(stop.zipCode)];
    case 'contact':
      return [text(stop.contactName)];
    case 'phone':
      return [text(stop.primaryPhone), text(stop.secondaryPhone)];
  }
}

/** Compares two customers for one column. Blank values go last in either direction. */
export function compareStops(a: Stop, b: Stop, sort: SortState): number {
  const partsA = sortParts(a, sort.key);
  const partsB = sortParts(b, sort.key);
  const blankA = partsA[0] === '';
  const blankB = partsB[0] === '';
  if (blankA !== blankB) return blankA ? 1 : -1;
  const sign = sort.direction === 'descending' ? -1 : 1;
  for (let index = 0; index < partsA.length; index += 1) {
    const result = collator.compare(partsA[index] ?? '', partsB[index] ?? '');
    if (result !== 0) return result * sign;
  }
  return 0;
}

/** Returns a sorted copy; ties keep the route order so results are predictable. */
export function sortStops<T extends Stop>(stops: readonly T[], sort: SortState | null): T[] {
  if (!sort) return [...stops];
  return stops
    .map((stop, index) => ({ stop, index }))
    .sort((a, b) => compareStops(a.stop, b.stop, sort) || a.index - b.index)
    .map((entry) => entry.stop);
}

/** Clicking a column cycles ascending → descending → route order. */
export function nextSort(current: SortState | null, key: SortKey): SortState | null {
  if (current?.key !== key) return { key, direction: 'ascending' };
  if (current.direction === 'ascending') return { key, direction: 'descending' };
  return null;
}

export function describeSort(sort: SortState | null): string {
  if (!sort) return 'Route order';
  return `${SORT_LABELS[sort.key]}, ${sort.direction === 'ascending' ? 'A to Z' : 'Z to A'}`;
}

/**
 * Splits a search box value into lower-case terms. Every term must match
 * somewhere, so "deli brooklyn" finds a deli in Brooklyn even though no
 * single field contains both words.
 */
export function searchTerms(raw: string): string[] {
  return raw.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
}

/** A term written like a phone number ("555-0111", "(555) 0111") is compared digit by digit. */
function phoneDigits(term: string): string {
  return /^[\d\s().+-]+$/.test(term) ? digitsOf(term) : '';
}

/**
 * A customer matches when each term appears in the name, street address,
 * city, state, ZIP code, contact name, or a phone number. Matching is a
 * case-insensitive "contains" check; phone terms also match with the
 * punctuation removed.
 */
export function matchesCustomer(stop: Stop, terms: readonly string[]): boolean {
  if (terms.length === 0) return true;
  const fields = [
    stop.name,
    stop.streetAddress,
    stop.city,
    stop.state,
    stop.zipCode,
    stop.contactName,
    stop.primaryPhone,
    stop.secondaryPhone,
  ].map(lower);
  const phones = [stop.primaryPhone, stop.secondaryPhone].map(digitsOf).filter(Boolean);
  return terms.every((rawTerm) => {
    const term = rawTerm.toLocaleLowerCase();
    if (fields.some((field) => field.includes(term))) return true;
    const digits = phoneDigits(term);
    return digits !== '' && phones.some((phone) => phone.includes(digits));
  });
}

/**
 * A route matches when each term appears in its number or its distributor's
 * name; the word "route" itself is ignored so "route 15" works. A matching
 * route shows all of its customers.
 */
export function matchesRoute(
  routeNumber: string,
  distributorName: string,
  terms: readonly string[],
): boolean {
  if (terms.length === 0) return true;
  const fields = [routeNumber, distributorName].map(lower);
  return terms.every((rawTerm) => {
    const term = rawTerm.toLocaleLowerCase();
    return term === 'route' || fields.some((field) => field.includes(term));
  });
}

export function cityStateZip(stop: Stop): string {
  return [text(stop.city), text(stop.state), text(stop.zipCode)].filter(Boolean).join(' ');
}

/** "100 Sample Street, Exampleton MA 02134" — the text a map service can geocode. */
export function fullAddress(stop: Stop): string {
  return [text(stop.streetAddress), cityStateZip(stop)].filter(Boolean).join(', ');
}

export function hasValidCoordinates(
  stop: Stop,
): stop is Stop & { latitude: number; longitude: number } {
  const { latitude, longitude } = stop;
  return (
    typeof latitude === 'number' &&
    typeof longitude === 'number' &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180 &&
    !(latitude === 0 && longitude === 0)
  );
}

const MAP_SEARCH_URL = 'https://www.google.com/maps/search/?api=1&query=';

/**
 * Google Maps link for one customer: the stored coordinates when they are
 * valid, otherwise the complete address, percent-encoded. Null when there
 * is nothing to look up. The link is only followed when a person clicks it;
 * the page itself never contacts the map service.
 */
export function mapLink(stop: Stop): string | null {
  if (stop.googlePlaceId) {
    return `${MAP_SEARCH_URL}${encodeURIComponent(fullAddress(stop) || stop.name)}&query_place_id=${encodeURIComponent(stop.googlePlaceId)}`;
  }
  if (hasValidCoordinates(stop)) {
    const round = (value: number) => String(Number(value.toFixed(6)));
    return `${MAP_SEARCH_URL}${encodeURIComponent(`${round(stop.latitude)},${round(stop.longitude)}`)}`;
  }
  const address = fullAddress(stop);
  return address ? `${MAP_SEARCH_URL}${encodeURIComponent(address)}` : null;
}
